/**
 * LinkedIn URL handling, shared by every field that accepts one.
 *
 * The backend canonicalizes on write (`src/python/utils/linkedin.py`), storing the bare form
 * `linkedin.com/in/jane-doe` — no scheme, no `www.`, no query/fragment, no trailing slash.
 * That is the form MDM enrichment joins on, so it is also the form that comes back to the
 * client.
 *
 * This module exists because five call sites had grown three mutually contradictory rules:
 * the People grid required `https://`, the Companies grid accepted anything containing
 * "linkedin.com", and the detail sheets made the scheme optional. The People grid's rule in
 * particular rejected the very value the backend stores, so re-committing an untouched cell
 * failed validation.
 *
 * Input is accepted leniently — scheme optional, `www.` optional, query/fragment and
 * trailing slashes tolerated — because people paste straight from the browser. Validation is
 * still strict about the shape: it must be a linkedin.com profile/company path, not merely a
 * string containing "linkedin.com".
 *
 * `normalizeLinkedinUrl` is a validation helper, not a write transform — values are sent to
 * the backend as typed and canonicalized there, so there is one authority on the stored form.
 */

/** `linkedin.com/<in|pub|company>/<slug>[/<extra>...]`, applied post-normalization. */
const LINKEDIN_PATH_RE = /^linkedin\.com\/(in|pub|company)\/[^\s/?#]+(\/[^\s/?#]+)*$/i;

/**
 * Reduce a LinkedIn URL to the backend's canonical form: scheme, `www.`, query, fragment and
 * surrounding slashes removed. Returns "" for empty or unusable input.
 *
 * Mirrors `normalize_linkedin_url` in src/python/utils/linkedin.py.
 */
export function normalizeLinkedinUrl(value: string | null | undefined): string {
  if (!value) return "";
  let result = value.trim();
  if (!result) return "";
  try {
    result = decodeURIComponent(result);
  } catch {
    // Malformed percent-encoding — keep the raw string rather than throwing. The backend's
    // unquote() is equally forgiving.
  }
  result = result.normalize("NFC");
  result = result.replace(/^https?:\/\//i, "");
  result = result.replace(/^www\./i, "");
  result = result.split("?")[0].split("#")[0];
  result = result.replace(/^\/+|\/+$/g, "");
  return result;
}

/** Whether `value` is a LinkedIn profile or company URL in any of the accepted shapes. */
export function isValidLinkedinUrl(value: string | null | undefined): boolean {
  return LINKEDIN_PATH_RE.test(normalizeLinkedinUrl(value));
}

/**
 * Validator for the `validate` prop on EditableCell / EditableDetailField.
 *
 * Empty passes: an empty value is how a field is cleared, and blocking it would make the
 * clear impossible.
 */
export function validateLinkedinUrl(
  value: string | null | undefined,
  example = "linkedin.com/in/example",
): string | null {
  if (!value || !value.trim()) return null;
  return isValidLinkedinUrl(value)
    ? null
    : `Enter a valid LinkedIn URL (e.g., ${example})`;
}
